export const LicenseTypes = {
  b2b: 'b2b',
  marketplace: 'marketplace',
  wellhub: 'wellhub',
  ovationHealth: 'ovation-health',
} as const;

export type LicenseType = (typeof LicenseTypes)[keyof typeof LicenseTypes];
export type LicenseActivationMode = 'with-id' | 'without-id';
export type SignUpFlow =
  | 'standard'
  | 'generic-license'
  | 'wellhub-license'
  | 'ovation-health-license';

export const LicenseStorageKeys = {
  activationPending: 'license_activation_pending',
  email: 'license_email',
  licenseId: 'license',
  licenseType: 'license_type',
} as const;

export interface LicenseFlowConfig {
  readonly type: LicenseType;
  readonly activationMode: LicenseActivationMode;
  readonly signUpFlow: SignUpFlow;
  readonly companyId?: string;
}

const defaultConfig: LicenseFlowConfig = {
  type: LicenseTypes.b2b,
  activationMode: 'with-id',
  signUpFlow: 'generic-license',
};

const configs: Record<LicenseType, LicenseFlowConfig> = {
  [LicenseTypes.b2b]: defaultConfig,
  [LicenseTypes.marketplace]: {
    type: LicenseTypes.marketplace,
    activationMode: 'with-id',
    signUpFlow: 'generic-license',
  },
  [LicenseTypes.wellhub]: {
    type: LicenseTypes.wellhub,
    activationMode: 'with-id',
    signUpFlow: 'wellhub-license',
    companyId: '10000',
  },
  [LicenseTypes.ovationHealth]: {
    type: LicenseTypes.ovationHealth,
    activationMode: 'without-id',
    signUpFlow: 'ovation-health-license',
    companyId: '11000',
  },
};

function isLicenseType(type: string): type is LicenseType {
  return (Object.values(LicenseTypes) as readonly string[]).includes(type);
}

export function resolveLicenseType(type: string | null | undefined): LicenseType {
  return type !== null && type !== undefined && isLicenseType(type) ? type : LicenseTypes.b2b;
}

export function getLicenseFlowConfig(type: string | null | undefined): LicenseFlowConfig {
  return configs[resolveLicenseType(type)];
}

export function isOvationHealthLicense(type: string | null | undefined): boolean {
  return resolveLicenseType(type) === LicenseTypes.ovationHealth;
}
